import { pluralize } from 'utils/string';

export const INVITATIONS_NOT_SENT = 'Couldn’t send invitations';
export const INVITE_LINK_NOT_CREATED = 'Couldn’t create the invite link';
export const INVITE_LINK_NOT_UPDATED = 'Couldn’t update the invite link';
export const EMAILS_NOT_CHECKED = 'Couldn’t check the email addresses';
export const LINK_COPIED = 'Link copied to clipboard successfully';
export const ACCOUNT_USERS_NOT_LOADED = 'Couldn’t load the account’s users';
export const CUSTOM_MESSAGE_LABEL = 'Custom message';
export const CUSTOM_MESSAGE_TITLE = 'Add a custom message to your invitations';
// Under HubSpot's 256-character property limit, past which messages get cut off.
export const MESSAGE_MAX_LENGTH = 255;

export const removeChipLabel = (name: string): string => `Remove ${name}`;

export const managerOfLabel = (teamName: string): string => `Manager of ${teamName}`;

// "Client Success" reads "Manager of Client Success Team"; "Product Team" doesn't repeat it.
export const managerOfTeamLabel = (teamName: string): string =>
  managerOfLabel(/\bteam$/i.test(teamName.trim()) ? teamName : `${teamName} Team`);

export const INVITE_TABS_COPY = {
  BY_EMAIL: 'Invite by email',
  BY_LINK: 'Invite by link',
} as const;

export const TEAM_SELECT_PLACEHOLDER = 'Select a team';

// Today's tab labels, kept while the Skill Dive invite flow is flagged off.
export const LEGACY_INVITE_TABS_COPY = {
  BY_EMAIL: 'Invite by Email',
  BY_LINK: 'Invite by Link',
} as const;

export const INVITE_LINK_COPY = {
  TITLE: 'Invite link',
  NOT_CREATED: 'The invite link wasn’t created.',
  RETRY: 'Retry',
  COPY: 'Copy',
  COPY_INVITE_LINK: 'Copy invite link',
  TEAM_ROLE: 'Team role',
  TEAM_MANAGER: 'Team manager',
  ASSIGN_ACCESS: 'Assign access',
  ASSIGN_ACCESS_TO_NEW_USERS: 'Assign access to new users',
  TRAINING_LICENSE: 'Training license',
  SKILL_DIVE_LICENSE: 'Skill Dive license',
  INVITE_TO_TEAM: 'Invite to team',
  OPTIONAL: '(optional)',
  SELECTED_TEAMS: 'Selected teams',
  MANAGER_SUFFIX: ' | Manager',
} as const;

export const inviteLinkDescription = (forTeam: boolean): string =>
  `Anyone with this link and an approved email will be able to join your ${
    forTeam ? 'team' : 'account'
  }.`;

export const TEAM_PICKER_COPY = {
  LABEL: 'Teams',
  SEARCH_LABEL: 'Search teams',
  PLACEHOLDER: 'Search teams...',
  NO_TEAMS: 'No teams found',
  MANAGER: 'Manager',
} as const;

export const USER_PICKER_COPY = {
  LABEL: 'Users',
  SEARCH_LABEL: 'Search users',
  PLACEHOLDER: 'Search by name or email',
  LOADING: 'Loading users…',
  FAILED: 'Users couldn’t be loaded',
  EVERYONE_ON_TEAM: 'Everyone in your account is already on this team',
  NO_TEAM_USERS_TO_ADD: 'No existing users to add. Invite new users by email below.',
  NO_USERS: 'No users found',
} as const;

export const SELECT_USERS_COPY = {
  EXISTING_USERS_TITLE: 'Select existing users',
  SELECTED_USERS: 'Selected users',
  OR: 'or',
  EMAILS_TITLE: 'Add email addresses',
  EMAILS_DESCRIPTION: 'Invite new users to create an account and join your team.',
  EMAILS_HELPER: 'Add multiple emails separated by a comma (,).',
} as const;

export const INVITE_OUTCOME_COPY = {
  TITLE: 'Some changes didn’t go through',
  INTRO: 'Here’s what was saved and what wasn’t.',
  EXISTING_USERS: 'Existing users',
  NEW_USERS: 'New users',
} as const;

export const TEAM_INVITE_WIZARD_COPY = {
  TEAM_CREATED_TITLE: 'Your team has been created!',
  NEXT: 'Next',
  REMOVE_USERS: 'Remove Users',
  SAVE_CHANGES: 'Save changes',
  SEND_INVITES: 'Send Invites',
  DONE: 'Done',
  INVITE_LATER: 'I’ll do this later',
} as const;

export const inviteUsersToTeamTitle = (teamName: string): string =>
  `Invite users to join ${teamName}`;

export const invalidEmailError = (reason?: string): string =>
  `Failed: ${reason || 'Enter a valid email address.'}`;

export const invitationsSentLine = (newUsers: number): string =>
  `Invitations sent to ${pluralize(newUsers, 'new user')}`;

export const invitationsNotSentLine = (newUsers: number): string =>
  `Invitations couldn’t be sent to ${pluralize(newUsers, 'new user')}`;

export const activeUsersInvitedToTeam = (users: number, teamName: string): string =>
  `${pluralize(users, 'active user')} invited to team ${teamName}`;

export const activeUsersNotInvitedToTeam = (users: number, teamName: string): string =>
  `${pluralize(users, 'active user')} couldn’t be invited to team ${teamName}`;

export const pendingUsersInvitedToTeam = (users: number, teamName: string): string =>
  `${pluralize(users, 'pending user')} invited to team ${teamName}`;

export const addedToTeamLine = (teamName: string): string => `Added to ${teamName}`;

export const notAddedToTeamLine = (teamName: string): string => `Couldn’t be added to ${teamName}`;

export const madeManagerLine = (teamName: string): string => `Made a manager of ${teamName}`;

export const notMadeManagerLine = (teamName: string): string =>
  `Not made a manager of ${teamName}, since their licenses couldn’t be changed`;

export const managerChangeFailedLine = (teamName: string): string =>
  `Couldn’t be made a manager of ${teamName}`;

export const usersNotMadeManagers = (users: number, teamName: string): string =>
  `${pluralize(users, 'active user')} couldn’t be made ${
    users === 1 ? 'a manager' : 'managers'
  } of team ${teamName}`;
